import { useEffect, useRef } from 'react';
import { useTour } from '../../../contexts/TourContext';
import { useCheckoutStore } from '../../account-settings/subscriptions/checkout-v2/store';
import { useInvoicePreviewStore } from '../../account-settings/subscriptions/checkout-v2/store/useInvoicePreviewStore';
import { WalkthroughStop, WALKTHROUGH_BY_STEP } from '../copierWalkthrough';
import { TOUR_STEP, TourStepKey } from '../tourSteps';
import { anchorSelector } from '../tourAnchors';
import { waitForAnchor, waitForStill } from '../waitForAnchor';
import { HighlightTracker, claimDriver, releaseDriver, trackHighlighted } from '../tourDriver';
import { ACCORDION_SETTLE_MS } from '../tourTiming';

// Once a step's drawer or panel is open, walk its sections in order and explain
// every option. driver.js drives the sequence (next/prev/progress); the copy
// lives in copierWalkthrough.ts, mirroring the product docs.
const SectionWalkthrough = ({
  stepKey,
  stops: stopsProp,
  ready
}: {
  stepKey: TourStepKey | undefined;
  /** Explicit stops instead of the step's own (the payment instance). */
  stops?: WalkthroughStop[];
  /** Extra readiness gate for an explicit set (e.g. the drawer is on Payment). */
  ready?: boolean;
}) => {
  const { active, phase } = useTour();
  const checkoutOpen = useCheckoutStore((s) => s.drawerOpen);
  const stops = stopsProp ?? (stepKey ? WALKTHROUGH_BY_STEP[stepKey] : undefined);
  // The billing sections live in a keepMounted drawer, so existence is not
  // enough — wait until the user has actually opened it, or the walkthrough
  // would run before they ever pressed the purchase button.
  const panelReady = (stepKey === TOUR_STEP.plans ? checkoutOpen : true) && (ready ?? true);
  // The plans step never hands off to a page — its drawer opens from the
  // ticket — so the open drawer is the whole signal there.
  const armed = active && (phase === 'page' || stepKey === TOUR_STEP.plans) && !!stops && panelReady;
  const startedRef = useRef(false);

  useEffect(() => {
    if (!armed) {
      startedRef.current = false;
      return undefined;
    }

    if (!stops) return undefined;
    const owner = Symbol('section-walkthrough');
    let cancelled = false;
    let drv: Awaited<ReturnType<typeof claimDriver>> = null;
    let settle: number | undefined;
    let tracker: HighlightTracker | null = null;

    // The walkthrough only makes sense once the panel's first section exists —
    // these drawers mount well after the click that opened them.
    const wait = waitForAnchor(stops[0].anchor, async (first) => {
      if (cancelled || startedRef.current) return;
      startedRef.current = true;

      // The panel is still sliding in when its anchor first reads visible —
      // let it land before anything is placed against it.
      await waitForStill(first, () => cancelled);
      if (cancelled) return;

      // The invoice preview lands late and moves the drawer's keys down. Read
      // it imperatively and wait, rather than gating the effect: a loading
      // flip must not tear a running walkthrough down and restart it.
      const settled = Date.now() + 2500;
      while (!cancelled && useInvoicePreviewStore.getState().loading && Date.now() < settled) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => {
          window.setTimeout(r, 80);
        });
      }
      if (cancelled) return;

      drv = await claimDriver(owner, {
        showProgress: true,
        // Clear of the drawer edge and its shadow; the default 10 left the
        // popover hugging the panel.
        popoverOffset: 16,
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Got it',
        // Allow a re-run: if they close the panel and open it again, the
        // walkthrough should be there rather than silently spent.
        onDestroyed: () => {
          startedRef.current = false;
        },
        onHighlightStarted: (el) => {
          // A new stop: track this element, and hold still while driver
          // settles it.
          tracker?.step(el as Element | undefined);
          const root = el as HTMLElement | undefined;
          if (!root || root.classList.contains('Mui-expanded')) return;
          // The sections are collapsed accordions and mutually exclusive, so
          // open the one being explained — a spotlight on a closed header
          // teaches nothing. Only THEN is a re-place needed: scheduling one for
          // a plain control (the pay key) just repaints the card for nothing.
          const summary = root.querySelector<HTMLElement>('.MuiAccordionSummary-root');
          if (!summary) return;
          summary.click();
          settle = window.setTimeout(() => drv?.refresh(), ACCORDION_SETTLE_MS);
        },
        // Bring a target that is off-screen into view; placement itself is
        // driver's, and the tracker corrects it only if the target moves.
        onHighlighted: (el) => {
          const target = el as HTMLElement | undefined;
          if (!target || typeof target.scrollIntoView !== 'function') return;
          const r = target.getBoundingClientRect();
          if (r.top < 0 || r.bottom > window.innerHeight) target.scrollIntoView({ block: 'center' });
        },
        steps: stops.map((s: WalkthroughStop) => ({
          element: anchorSelector(s.anchor),
          popover: {
            title: s.title,
            description: `${s.description}<p style="margin:10px 0 0"><a href="${s.docsUrl}" target="_blank" rel="noopener noreferrer">${s.docsLabel} ↗</a></p>`,
            side: s.side || 'left',
            align: 'center'
          }
        }))
      });
      if (cancelled || !drv) return;
      drv.drive();
      // The drawer's preview loads late and pushes its keys down; follow it.
      tracker = trackHighlighted(drv);
      tracker.step(document.querySelector(anchorSelector(stops[0].anchor)));
    });

    return () => {
      cancelled = true;
      wait.cancel();
      window.clearTimeout(settle);
      tracker?.dispose();
      releaseDriver(owner);
      drv = null;
    };
  }, [armed, stops]);

  return null;
};

export default SectionWalkthrough;
